'use client';

import { motion, useReducedMotion } from 'motion/react';

import { REVEAL_DURATION, REVEAL_EASE, REVEAL_STEP, REVEAL_VIEWPORT } from '@/components/reveal';
import { TIMELINE_COPY, TIMELINE_DAYS } from '@/lib/content';
import { cn, HOVER_WASH } from '@/lib/utils';

/** The four days and the payoff are one sequence; only the last one is branded. */
const ROWS = [
  ...TIMELINE_DAYS.map(day => ({ ...day, live: false })),
  {
    n: 'live',
    dow: TIMELINE_COPY.liveDow,
    name: TIMELINE_COPY.liveName,
    body: TIMELINE_COPY.liveBody,
    live: true,
  },
];

/**
 * Ruled columns rather than cards: the rules are the ledger, so the grid has no gap and each column
 * carries its own left rule, with the closing rule on the list so every row closes once the grid
 * wraps. Those rules draw left to right, column by column, and the text fills in behind them.
 *
 * The client boundary stops here; the section around it stays server-rendered.
 */
export function TimelineRows() {
  const reducedMotion = useReducedMotion();

  return (
    <ol className="grid border-r sm:grid-cols-2 lg:grid-cols-4">
      {ROWS.map((row, index) => (
        <li key={row.n} className={cn('relative border-l px-5.5 pt-6.5 pb-7.5', HOVER_WASH)}>
          {/* The 3px rule as an element, so it can be drawn rather than just turned on. */}
          <motion.span
            aria-hidden="true"
            className={cn(
              'absolute inset-x-0 top-0 h-[3px] origin-left',
              row.live ? 'bg-brand' : 'bg-foreground',
            )}
            data-reveal=""
            initial={reducedMotion ? false : { scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={REVEAL_VIEWPORT}
            transition={{
              duration: REVEAL_DURATION,
              ease: REVEAL_EASE,
              delay: index * REVEAL_STEP,
            }}
          />
          <motion.div
            data-reveal=""
            initial={reducedMotion ? false : { opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={REVEAL_VIEWPORT}
            transition={{
              duration: REVEAL_DURATION,
              ease: REVEAL_EASE,
              delay: index * REVEAL_STEP + 0.22,
            }}
          >
            <p
              className={cn(
                'font-mono text-2xs tracking-label transition-colors duration-300',
                row.live ? 'text-brand' : 'text-faint-foreground group-hover:text-muted-foreground',
              )}
            >
              {row.dow}
            </p>
            {/* text-xl and text-sm, where the other ruled lists on the page run text-2xl and
                text-base: four columns share the band here, so each gets a quarter of the measure
                the full-width rows get. */}
            <h3 className="mt-3.5 mb-2.5 text-xl">{row.name}</h3>
            <p className="text-sm leading-relaxed text-muted-foreground transition-colors duration-300 group-hover:text-foreground group-active:text-foreground">
              {row.body}
            </p>
          </motion.div>
        </li>
      ))}
    </ol>
  );
}
